<template>
  <div>
    <div class="d-flex justify-content ml-2 align-items-center my-2">
      <button
        class="btn btn-sm rounded-circle p-25 cursor-pointer"
        :class="isDark ? 'btn-danger' : 'btn-primary'"
        @click="goBack()"
      >
        <i class="feather icon-arrow-left arrow-icon"></i>
      </button>
      <div
        class="ml-1 text-capitalize text-dark cursor-pointer goback-text"
        @click="goBack()"
      >
        {{ $t("actions.go_back") }}
      </div>
    </div>
    <!-- Table Container Card -->
    <b-card class="m-2" no-body>
      <b-card-header class="pb-50">
        <h5 class="text-capitalize">
          <span v-if="!isUpdating">{{ $t("actions.add") }}</span>
          <span v-else>{{ $t("actions.update") }}</span>
        </h5>
      </b-card-header>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <div class="m-2">
            <b-row>
              <!-- Tenant Selection -->
              <b-col cols="12" md="4" class="mb-1 mb-md-0">
                <label class="d-block">{{ $tc("tenants.title", 1) }}</label>
                <v-select
                  :options="tenantOptions"
                  v-model="idTenant"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :reduce="(val) => val.value"
                  :placeholder="$t('custom_forms.select_tenant')"
                  class="w-100"
                  append-to-body
                />
              </b-col>

              <!-- Template Name -->
              <b-col cols="12" md="8">
                <b-row>
                  <b-col class="mb-1 mb-md-0">
                    <validation-provider
                      #default="validationContext"
                      name="Template name"
                      rules="required"
                    >
                      <label class="d-block">
                        {{ $t("risk.template.name") }}
                      </label>
                      <b-form-input
                        v-model="templateName"
                        class="d-inline-block mr-1"
                        :placeholder="$t('risk.template.name')"
                      />
                      <b-form-invalid-feedback
                        :state="getValidationState(validationContext)"
                      >
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-col>

                  <b-col class="mb-1 mb-md-0"></b-col>
                  <b-col cols="2" class="mb-1 mb-md-0"></b-col>
                </b-row>
              </b-col>

              <b-col
                cols="12"
                md="12"
                class="d-flex flex-column align-items-end justify-content-end mb-1 mb-md-0 mt-2"
              >
                <div>
                  <!-- Add new section (NUEVO) -->
                  <b-button class="mr-1" variant="secondary" @click="onAddSection">
                    <span class="text-nowrap text-capitalize">
                      {{ $t("risk.template.add_section") }}
                    </span>
                  </b-button>

                  <!-- Add new item -->
                  <b-button class="mr-1" variant="primary" @click="addItem()">
                    <span class="text-nowrap text-capitalize">
                      {{ $t("risk.template.add_new_field") }}
                    </span>
                  </b-button>
                  <!-- Save changes -->
                  <b-button variant="success" @click="onSave()" :disabled="!isValid">
                    <span class="text-nowrap text-capitalize">
                      {{ $t("actions.save") }}
                    </span>
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </div>
        </validation-observer>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              md="12"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <div class="w-100">
                <div class="">
                  <risk-template-builder
                    ref="builderRef"
                    :items.sync="items"
                    :is-valid.sync="isValid"
                  />
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BFormInvalidFeedback,
} from "bootstrap-vue";

import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import useTemplateBuilderConfig from "./useTemplateBuilderConfig";
import store from "@/store";
import { computed, ref, onMounted } from "@vue/composition-api";
import RiskTemplateBuilder from "@/components/riskTemplateBuilder";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BFormInvalidFeedback,
    vSelect,
    RiskTemplateBuilder,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  setup(_, context) {
    const {
      addItem,
      save,
      isValid,
      items,
      templateName,
      idTenant,
      refFormObserver,
      getValidationState,
      required,
      isUpdating,
    } = useTemplateBuilderConfig();

    const builderRef = ref(null);
    const isDark = computed(() => store.state.appConfig.layout.skin === "dark");
    const goBack = () => context.root.$router.push({ name: "riskTemplates" });

    onMounted(() => {
      store.dispatch("tenant/getAll");
    });

    const tenantOptions = computed(() => store.getters["tenant/getSelectedItems"]);


    //Override of onsave to store tenant
    const onSave = async () => {
      const validation = await refFormObserver.value.validate();
      if (!validation) return;
      await save({ id_tenant: idTenant.value });
    };

    const onAddSection = () => {
      // call public method of builder
      builderRef.value && builderRef.value.addSection && builderRef.value.addSection();
    };

    return {
      goBack,
      isDark,
      addItem,
      save,
      onSave,
      isValid,
      items,
      templateName,
      refFormObserver,
      getValidationState,
      required,
      isUpdating,
      tenantOptions,
      idTenant,
      builderRef,
      onAddSection,
    };
  },
};
</script>

<style lang="scss" scoped>
.arrow-icon {
  font-size: 12px;
}
.goback-text {
  position: relative;
  top: 0.1rem;
  margin-left: 0.5rem !important;
}
</style>
